import React, { useState, useEffect } from 'react';
import { editWorkOrder, fetchWorkOrderInvoicePdf } from './api';
import './ActionBar.css';
import './workorder.css'
import WorkOrderHistory from './workorderhistory';
import JobOrder from './jobs/joborder';
import InvoiceModal from './InvoiceModal';
import PrintInvoice from './PrintInvoice';
import SendInvoiceDialog from './SendInvoiceDialog';
import { printPdf } from '../utils/printPdf';
import { useNavigate } from 'react-router-dom';
import { formatDateTimeDisplay } from '../utils/timezone';
import CarDiagram from './CarDiagram';
import TireDisposalToggle from './TireDisposalToggle';
import WorkOrderSubscriptionButton from './WorkOrderSubscriptionButton';
import PaymentBadges from './PaymentBadges';

const WorkOrder = ({ workOrder, onClose, fromJobOrder }) => {
  const [currentWorkOrder, setCurrentWorkOrder] = useState({
    ...workOrder,
    straightening_list: workOrder.straightening_list_display || workOrder.straightening_list || []
  });
  const [editedWorkOrder, setEditedWorkOrder] = useState(() => {
    const originalPrice = workOrder.price || 0;
    const paymentMethod = workOrder.payment_method || 'credit_card';
    
    // Use the price as-is from the database since it should already have the correct fee applied
    return { 
      ...workOrder, 
      price: originalPrice,
      straightening_list: workOrder.straightening_list_display || workOrder.straightening_list || []
    };
  });
  const [showHistory, setShowHistory] = useState(false);
  const [showJobOrder, setShowJobOrder] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // true when the invoice modal edits the existing QuickBooks invoice instead of creating one
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [showPrintInvoice, setShowPrintInvoice] = useState(false);
  const [loadingInvoicePdf, setLoadingInvoicePdf] = useState(false);
  const [showSendInvoice, setShowSendInvoice] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(workOrder.payment_method || 'check');
  const [displayPrice, setDisplayPrice] = useState(workOrder.price || 0); // Price shown in input
  const [feeApplied, setFeeApplied] = useState(() => {
    // Fee is applied if payment method is credit card
    return (workOrder.payment_method || 'check') === 'credit_card';
  });
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [showMobileMoreDropdown, setShowMobileMoreDropdown] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setEditedWorkOrder({ ...editedWorkOrder, [e.target.name]: e.target.value });
    setHasUnsavedChanges(true);
  };


  const handlePriceChange = (e) => {
    const value = e.target.value;
    
    // Allow empty string for user to clear and type new number
    if (value === '') {
      setDisplayPrice('');
      setEditedWorkOrder({ ...editedWorkOrder, price: '' });
      setFeeApplied(false); // Reset fee status when user clears field
    } else {
      // Validate input to allow only numbers with up to 2 decimal places
      const decimalRegex = /^\d*\.?\d{0,2}$/;
      
      if (decimalRegex.test(value)) {
        const newPrice = parseFloat(value) || 0;
        setDisplayPrice(value); // Keep the string value to preserve decimal places
        setEditedWorkOrder({ ...editedWorkOrder, price: newPrice });
        // If credit card is selected, mark fee as applied
        if (paymentMethod === 'credit_card') {
          setFeeApplied(true);
        } else {
          setFeeApplied(false);
        }
      }
      // If the input doesn't match the regex, don't update the state (ignore the input)
    }
    setHasUnsavedChanges(true);
  };

  const handlePriceBlur = (e) => {
    const value = e.target.value;
    if (value !== '' && !isNaN(parseFloat(value))) {
      // Format to 2 decimal places when user finishes typing
      const formattedPrice = parseFloat(value).toFixed(2);
      setDisplayPrice(formattedPrice);
      setEditedWorkOrder({ ...editedWorkOrder, price: parseFloat(formattedPrice) });
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);

    // Once invoiced, the price comes from the invoice (use Edit Invoice), so only the method changes
    if (currentWorkOrder.is_invoiced) {
      setEditedWorkOrder({ ...editedWorkOrder, payment_method: method });
      setHasUnsavedChanges(true);
      return;
    }
    const currentPrice = displayPrice;
    
    // Don't calculate if price is empty or invalid
    if (currentPrice === '' || currentPrice === null || currentPrice === undefined) {
      setFeeApplied(method === 'credit_card');
      return;
    }
    
    let newDisplayPrice;
    
    if (method === 'credit_card') {
      // Add exact 3% fee only if not already applied
      if (!feeApplied) {
        newDisplayPrice = parseFloat((currentPrice * 1.03).toFixed(2));
        setFeeApplied(true);
      } else {
        newDisplayPrice = currentPrice; // Fee already applied
      }
    } else {
      // Remove 3% fee (convert back to base price) only if fee was applied
      if (feeApplied) {
        newDisplayPrice = parseFloat((currentPrice / 1.03).toFixed(2));
        setFeeApplied(false);
      } else {
        newDisplayPrice = currentPrice; // No fee to remove
      }
    }
    
    // Update display price and edited work order
    setDisplayPrice(newDisplayPrice);
    setEditedWorkOrder({ ...editedWorkOrder, price: newDisplayPrice });
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => onClose(currentWorkOrder)); // Close with the saved work order, not edited
    } else {
      onClose(currentWorkOrder); // Close with the saved work order (e.g. invoice since emailed), not edited
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (pendingAction) {
      pendingAction();
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
    setPendingAction(null);
  };

  const showError = (message, title = 'Cannot Unmark as Paid') => {
    setErrorTitle(title);
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  const priceLockedTitle = currentWorkOrder.is_invoiced
    ? 'Paid in QuickBooks - change the price in QuickBooks'
    : 'Marked as paid - unmark as paid to change the price';
  const canEditInvoice = currentWorkOrder.is_invoiced && !currentWorkOrder.mark_as_paid;

  // Archiving waits for Save, so say so until then
  const archivePending = !!editedWorkOrder.is_archived !== !!currentWorkOrder.is_archived;
  const archiveLabel = archivePending
    ? (editedWorkOrder.is_archived ? 'Archives on Save' : 'Unarchives on Save')
    : (editedWorkOrder.is_archived ? 'Unarchive' : 'Archive');
  const archiveTitle = !currentWorkOrder.invoice_number
    ? 'Cannot archive without invoice number'
    : archivePending ? 'Click to undo' : '';

  // While a pop-up is open the action bar drops behind it, so it can't be clicked
  const popupOpen = showHistory || showInvoiceModal || showPrintInvoice || showSendInvoice ||
    showUnsavedWarning || showErrorModal;
  const priceTitle = currentWorkOrder.price_locked
    ? priceLockedTitle
    : (canEditInvoice ? 'Invoiced - use Edit Invoice to change the price' : '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Log the current state for debugging
      console.log('Current workOrder:', workOrder);
      console.log('Current editedWorkOrder:', editedWorkOrder);

      // Get customer ID from various possible sources
      const customerId = workOrder.customer;
      
      console.log('Found customerId:', customerId);

      if (!customerId) {
        console.error('No customer ID found in workOrder:', workOrder);
        alert('Customer information is missing. Please try again.');
        return;
      }

      // Only include straightening_list if it has changed
      const originalStraightening = workOrder.straightening_list_display || workOrder.straightening_list || [];
      const currentStraightening = editedWorkOrder.straightening_list || [];
      const straighteningChanged = JSON.stringify(originalStraightening.sort()) !== JSON.stringify(currentStraightening.sort());
      
      const dataToSubmit = {
        ...editedWorkOrder,
        customer: customerId,
        customer_name: workOrder.customer_name,
        price: parseFloat(editedWorkOrder.price) || 0,
        payment_method: paymentMethod
      };
      
      // Only include straightening_list if it has actually changed
      if (straighteningChanged) {
        dataToSubmit.straightening_list = currentStraightening;
      }


      const updatedWorkOrder = await editWorkOrder(workOrder.id, dataToSubmit);
      setHasUnsavedChanges(false);
      // e.g. QuickBooks added tax, so the saved price is the invoice total
      if (updatedWorkOrder.notice) {
        alert(updatedWorkOrder.notice);
      }
      if (onClose) {
        onClose(updatedWorkOrder);
      }
    } catch (error) {
      console.error('Error updating work order:', error);
      showError(error.response?.data?.error || 'Failed to update work order', 'Work Order Not Saved');
    }
  };

  const handleHistoryClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowHistory(true));
    } else {
      setShowHistory(true);
    }
  };

  const handleJobOrderClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowJobOrder(true));
    } else {
      setShowJobOrder(true);
    }
  };

  const handleConvertToInvoice = (e) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation(); // Stop event bubbling
    
    console.log('=== CONVERT TO INVOICE CLICKED ===');
    console.log('Has unsaved changes:', hasUnsavedChanges);
    console.log('Current work order:', currentWorkOrder);
    
    setEditingInvoice(false);
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowInvoiceModal(true));
    } else {
      setShowInvoiceModal(true);
    }
  };

  const handleEditInvoice = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setEditingInvoice(true);
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowInvoiceModal(true));
    } else {
      setShowInvoiceModal(true);
    }
  };

  // Invoiced through QuickBooks: print QuickBooks' own invoice. Otherwise (e.g. cash marked
  // paid with the generic number): the app's invoice layout.
  const printInvoice = () => {
    if (currentWorkOrder.is_invoiced) {
      printQuickBooksInvoice();
    } else {
      setShowPrintInvoice(true);
    }
  };

  const printQuickBooksInvoice = async () => {
    setLoadingInvoicePdf(true);
    try {
      await printPdf(() => fetchWorkOrderInvoicePdf(currentWorkOrder.id));
    } catch (error) {
      showError(error.response?.data?.error || 'Could not get the invoice from QuickBooks.', 'Invoice Not Available');
    } finally {
      setLoadingInvoicePdf(false);
    }
  };

  const handlePrintInvoice = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => printInvoice);
    } else {
      printInvoice();
    }
  };

  const handleSendInvoice = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowSendInvoice(true));
    } else {
      setShowSendInvoice(true);
    }
  };

  const handleInvoiceSent = (data) => {
    const sentFields = { invoice_sent_at: data.invoice_sent_at, invoice_sent_to: data.invoice_sent_to };
    setCurrentWorkOrder(prev => ({ ...prev, ...sentFields }));
    setEditedWorkOrder(prev => ({ ...prev, ...sentFields }));
  };

  const handleInvoiceConfirm = (invoiceData) => {
    console.log('=== WORK ORDER - handleInvoiceConfirm ===');
    console.log('Invoice data received:', invoiceData);
    
    // Check if this is a full work order update (from Mark as Paid) or just invoice data
    const isFullWorkOrderUpdate = invoiceData.id && invoiceData.status;
    console.log('Is full work order update:', isFullWorkOrderUpdate);
    
    let updatedWorkOrder;
    
    if (isFullWorkOrderUpdate) {
      // This is from "Mark as Paid" - use the full updated work order
      updatedWorkOrder = invoiceData;
      console.log('Using full work order update from Mark as Paid');
    } else {
      // This is from QuickBooks invoice creation - use the updated work order data from backend
      if (invoiceData.updated_work_order) {
        updatedWorkOrder = invoiceData.updated_work_order;
        console.log('Using updated work order from backend');
      } else {
        // Fallback to manual update if backend doesn't provide updated work order
        updatedWorkOrder = {
          ...currentWorkOrder,
          invoice_number: invoiceData.invoice_number,
          invoice_link: invoiceData.invoice_link,
          is_invoiced: true
        };
        console.log('Using manual invoice data update');
      }
    }
    
    console.log('Final updated work order:', updatedWorkOrder);
    
    // Update the local state
    setCurrentWorkOrder({
      ...updatedWorkOrder,
      straightening_list: updatedWorkOrder.straightening_list_display || updatedWorkOrder.straightening_list || []
    });
    setEditedWorkOrder(prev => ({
      ...prev,
      ...updatedWorkOrder,
      straightening_list: updatedWorkOrder.straightening_list_display || updatedWorkOrder.straightening_list || []
    }));
    
    // Update display price to match the updated work order price
    if (updatedWorkOrder.price) {
      setDisplayPrice(updatedWorkOrder.price);
    }
    
    // Close the invoice modal
    setShowInvoiceModal(false);
    
    // Pass the updated work order back to the parent
    console.log('Calling onClose with updated work order');
    if (onClose) {
      onClose(updatedWorkOrder);
    }
  };

  const handleCustomerClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => {
        localStorage.setItem('selectedCustomerId', workOrder.customer);
        navigate('/accounts');
      });
    } else {
      localStorage.setItem('selectedCustomerId', workOrder.customer);
      navigate('/accounts');
    }
  };

  const handlePhoneClick = () => {
    // Only allow calling on mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) return;
    
    const phoneNumber = editedWorkOrder.phone;
    if (phoneNumber && phoneNumber.trim()) {
      // Clean the phone number (remove any non-digit characters except +)
      const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
      // Use tel: protocol to trigger native calling interface
      window.location.href = `tel:${cleanPhone}`;
    }
  };

  const handleEditPhoneClick = () => {
    setIsEditingPhone(true);
  };

  const handlePhoneBlur = () => {
    setIsEditingPhone(false);
  };

  const handlePhoneKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditingPhone(false);
    }
  };

  const handleWheelSelection = (selectedWheels) => {
    setEditedWorkOrder(prev => ({
      ...prev,
      straightening_list: selectedWheels
    }));
    setHasUnsavedChanges(true);
  };

  const handleJobOrderClose = (updatedJob) => {
    if (updatedJob) {
      setEditedWorkOrder(prev => ({
        ...prev,
        job: updatedJob
      }));
      // Pass the updated work order back to the parent only if there are no unsaved changes
      if (onClose && !hasUnsavedChanges) {
        onClose({
          ...workOrder,
          job: updatedJob
        });
      }
    }
    setShowJobOrder(false);
  };

  const formatDate = (dateString) => {
    return formatDateTimeDisplay(dateString);
  };

  const handleMobileMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMobileMoreDropdown(!showMobileMoreDropdown);
  };

  // A choice in the phone "More" menu closes the menu, then runs (the handlers expect an event)
  const menuAction = (action) => () => {
    setShowMobileMoreDropdown(false);
    action({ preventDefault: () => {}, stopPropagation: () => {} });
  };

  const handleUnmarkAsPaid = async () => {
    // Check if this is a QuickBooks invoice
    const isQuickBooksInvoice = currentWorkOrder.invoice_link && 
                              currentWorkOrder.invoice_link.trim() && 
                              !currentWorkOrder.invoice_link.includes('PLACEHOLDER');
    
    if (isQuickBooksInvoice) {
      showError('Cannot unmark as paid for QuickBooks invoices. Please modify the invoice in QuickBooks instead.');
      return;
    }
    
    try {
      const updatedWorkOrder = await editWorkOrder(currentWorkOrder.id, {
        ...currentWorkOrder,
        mark_as_paid: false,
        invoice_number: null  // Clear the invoice number when unmarking as paid
      });
      setCurrentWorkOrder(updatedWorkOrder);
      setEditedWorkOrder(prev => ({
        ...prev,
        ...updatedWorkOrder
      }));
      if (onClose) {
        onClose(updatedWorkOrder);
      }
    } catch (error) {
      console.error('Error unmarking as paid:', error);
      alert(error.message || 'Failed to unmark as paid. Please try again.');
    }
  };

  // Archiving is saved with the rest of the work order, when Save is clicked
  const handleArchiveToggle = () => {
    if (!currentWorkOrder.invoice_number) {
      alert('Cannot archive work order without an invoice number. Please convert to invoice or mark as paid first.');
      return;
    }
    setEditedWorkOrder({ ...editedWorkOrder, is_archived: !editedWorkOrder.is_archived });
    setHasUnsavedChanges(true);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMoreDropdown && !event.target.closest('.actionbar-more')) {
        setShowMobileMoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMoreDropdown]);


  if (showJobOrder) {
    return (
      <div>
        {workOrder.job ? (
          <JobOrder 
            job={workOrder.job} 
            onClose={handleJobOrderClose} 
            fromWorkOrder={true}
          />
        ) : (
          <div className="no-job-message">
            <h3>No Job Order Associated</h3>
            <p>This work order does not have an associated job order.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    <form className='workorder' onSubmit={handleSubmit}>
      <div className="workorder-header-container">
        <h2 className='workorderheader'>Work Order #{workOrder.id}</h2>
        <div className="header-actions">
          <WorkOrderSubscriptionButton workOrder={workOrder} />
          <button type="button" className="close-button" onClick={handleClose}>×</button>
        </div>
      </div>
      <div className='workorderform'>
        <div className="workordertop">
          <div className='workordercontact'>
            <div className='workordercontactcolumn'>
              {/* Name */}
              <label className='workorderlabelcontact'>
                Name:
                <div 
                  className='workorderinputscontact customer-name-link'
                  onClick={handleCustomerClick}
                  style={{ cursor: 'pointer' }}
                >
                  {workOrder.customer_name}
                </div>
              </label>

              {/* Phone */}
              <label className='workorderlabelcontact'>
                Phone:
                <div className="phone-container">
                  {isEditingPhone ? (
                    <input
                      className='workorderinputscontact phone-input'
                      type="tel"
                      name="phone"
                      value={editedWorkOrder.phone}
                      onChange={handleChange}
                      onBlur={handlePhoneBlur}
                      onKeyDown={handlePhoneKeyDown}
                      pattern="[0-9]{10}"
                      placeholder="1234567890"
                      autoFocus
                    />
                  ) : (
                    <div className="phone-display-container">
                      <div 
                        className='workorderinputscontact phone-display'
                        onClick={handlePhoneClick}
                        style={{ cursor: 'pointer' }}
                        title="Click to call (mobile only)"
                      >
                        {editedWorkOrder.phone || 'No phone number'}
                      </div>
                      <button
                        type="button"
                        className="phone-edit-button"
                        onClick={handleEditPhoneClick}
                        title="Edit phone number"
                      >
                        ✎
                      </button>
                    </div>
                  )}
                </div>
              </label>
            </div>
            {/* Vehicle */}
            <label className='workorderlabelcontact'>
              Vehicle:
              <input
                className='workorderinputscontact'
                type="text"
                name="vehicle"
                value={editedWorkOrder.vehicle}
                onChange={handleChange}
              />
            </label>
          </div>
          

          <div className='workorderimportantinfo'>
            {/* Price */}
            <label className='workorderlabelimportantinfo'>
              Price:
              {currentWorkOrder.price_locked && (
                <span className="price-locked-indicator" title={priceLockedTitle}>
                  🔒
                </span>
              )}
              <div className="price-payment-container">
                <input
                  className={`workorderinputsprice ${currentWorkOrder.price_locked ? 'price-locked' : ''}`}
                  type="number"
                  step="0.01"
                  name="price"
                  value={displayPrice}
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  disabled={currentWorkOrder.price_locked || currentWorkOrder.is_invoiced}
                  title={priceTitle}
                />
                <div className={`payment-method-slider ${currentWorkOrder.price_locked ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className={`payment-method-btn ${paymentMethod === 'check' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('check')}
                    disabled={currentWorkOrder.price_locked}
                    title={currentWorkOrder.price_locked ? priceLockedTitle : 'Check'}
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className={`payment-method-btn credit-card ${paymentMethod === 'credit_card' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('credit_card')}
                    disabled={currentWorkOrder.price_locked}
                    title={currentWorkOrder.price_locked ? priceLockedTitle : 'Credit Card (+3%)'}
                  >
                    💳
                  </button>
                </div>
              </div>
            </label>
            <div>
              <label className='workorderlabelimportantinfo'>
                Date Created:
                <span className='workorderinputspricedatedcreated'>
                  {formatDate(editedWorkOrder.created_at)}
                </span>
              </label>

              {/* Date Promised */}
              <label className='workorderlabelimportantinfo'>
                Date Promised:
                <input
                  className='workorderinputsprice'
                  type="date"
                  name="date_promised"
                  value={editedWorkOrder.date_promised}
                  onChange={handleChange}
                />
              </label>
            </div>
            <label className='workorderlabelimportantinfo'>
              Status:
              <select
                className='workorderinputsprice'
                name="status"
                value={editedWorkOrder.status}
                onChange={(e) => setEditedWorkOrder({ ...editedWorkOrder, status: e.target.value })}
              >
                <option value="In Process">In Process</option>
                <option value="Called">Called</option>
                <option value="Picked Up">Picked Up</option>
              </select>
            </label>
            <label className='workorderlabelimportantinfo'>
              Invoice Number:
              <span className='workorderinputspricedatedcreated'>
                {currentWorkOrder.invoice_number ? (
                  currentWorkOrder.invoice_link && currentWorkOrder.invoice_link.trim() && !currentWorkOrder.invoice_link.includes('PLACEHOLDER') ? (
                    <a 
                      href={currentWorkOrder.invoice_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="invoice-link"
                      title="Open invoice in QuickBooks"
                    >
                      {currentWorkOrder.invoice_number}
                    </a>
                  ) : (
                    <span title="Invoice link not available - convert to invoice to get QuickBooks link">
                      {currentWorkOrder.invoice_number}
                    </span>
                  )
                ) : (
                  'Not created yet'
                )}
                <PaymentBadges workOrder={currentWorkOrder} showBalance />
              </span>
              {currentWorkOrder.invoice_sent_at && (
                <small className='invoice-sent-note'>
                  Emailed to {currentWorkOrder.invoice_sent_to} on {formatDateTimeDisplay(currentWorkOrder.invoice_sent_at)}
                </small>
              )}
            </label>
            

          </div>
        </div>
        <div className='workorderdetails'>
          <div className="workorderdetailssmall">
            {/* Service Requested */}
            <label className='workorderlabeldetails'>
              Service Requested:
              <select
                className='workorderinputsdetails'
                name="service_requested"
                value={editedWorkOrder.service_requested}
                onChange={(e) => setEditedWorkOrder({ ...editedWorkOrder, service_requested: e.target.value })}
              >
                <option value="Restoration">Restoration</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Straightening">Straightening</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>


            {/* Number of Wheels/Parts */}
            <label className='workorderlabeldetails'>
              Number of Wheels/Parts:
              <input
                className='workorderinputsdetails'
                type="number"
                name="number_of_wheels"
                value={editedWorkOrder.number_of_wheels}
                onChange={handleChange}
              />
            </label>

            {/* Tires, with Tire Disposal below it (beside it on mobile) */}
            <div className='workorder-tires-group'>
              <label className='workorderlabeldetails'>
                Tires:
                <select
                  className='workorderinputsdetails'
                  name="tires"
                  value={editedWorkOrder.tires}
                  onChange={(e) => setEditedWorkOrder({ ...editedWorkOrder, tires: e.target.value })}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="New">New</option>
                </select>
              </label>
              <label className='workorderlabeldetails'>
                Tire Disposal:
                <TireDisposalToggle
                  boxClassName='workorderinputsdetails'
                  checked={editedWorkOrder.tire_disposal}
                  onChange={(checked) => {
                    setEditedWorkOrder(prev => ({ ...prev, tire_disposal: checked }));
                    setHasUnsavedChanges(true);
                  }}
                />
              </label>
            </div>


            {/* Center Caps */}
            <label className='workorderlabeldetails'>
              Center Caps:
              <select
                className='workorderinputsdetails'
                name="center_caps"
                value={editedWorkOrder.center_caps}
                onChange={(e) => setEditedWorkOrder({ ...editedWorkOrder, center_caps: e.target.value })}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>


            {/* Straightening */}
            <label className='workorderlabeldetails'>
              Straightening:
              <CarDiagram
                selectedWheels={editedWorkOrder.straightening_list || []}
                onWheelSelection={handleWheelSelection}
                disabled={currentWorkOrder.price_locked}
              />
            </label>
          </div>

          <div className='workorderdetailslarge'>
            {/* Description */}
            <label className='workorderlabeldetailslarge'>
              Description:
              <textarea
                className='workorderinputsdetailslarge'
                name="description"
                value={editedWorkOrder.description}
                onChange={handleChange}
              />
            </label>

            {/* Notes */}
            <label className='workorderlabeldetailslarge'>
              Notes:
              <textarea
                className='workorderinputsdetailslarge'
                name="notes"
                value={editedWorkOrder.notes}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>
      </div>
      <div className={`actionbar workorder-actionbar ${popupOpen ? 'actionbar-behind' : ''}`}>
        {/* Desktop: work order actions, the invoice's actions, then archive and save */}
        <div className='actionbar-actions'>
          <button type='button' className='actionbar-btn' onClick={handleHistoryClick}>History</button>
          {!fromJobOrder && currentWorkOrder.job && (
            <button type='button' className='actionbar-btn' onClick={handleJobOrderClick}>View Job Order</button>
          )}
          <span className='actionbar-divider' />
          {!currentWorkOrder.invoice_number && (
            <button type='button' className='actionbar-btn actionbar-btn-convert' onClick={handleConvertToInvoice}>
              Convert to Invoice
            </button>
          )}
          {currentWorkOrder.invoice_number && (
            <div className='actionbar-invoice'>
              <span className='actionbar-label'>
                Invoice #{currentWorkOrder.invoice_number}{currentWorkOrder.mark_as_paid && !currentWorkOrder.is_invoiced ? ' · Paid' : ''}
              </span>
              <div className='actionbar-segmented'>
                {canEditInvoice && (
                  <button type='button' className='actionbar-btn' onClick={handleEditInvoice}>Edit</button>
                )}
                <button type='button' className='actionbar-btn' onClick={handlePrintInvoice} disabled={loadingInvoicePdf}>
                  {loadingInvoicePdf ? 'Loading…' : 'Print'}
                </button>
                {currentWorkOrder.is_invoiced && (
                  <button type='button' className='actionbar-btn' onClick={handleSendInvoice}>
                    {currentWorkOrder.invoice_sent_at ? 'Send Again' : 'Send'}
                  </button>
                )}
              </div>
              {currentWorkOrder.mark_as_paid && !currentWorkOrder.is_invoiced && (
                <button
                  type='button'
                  className='actionbar-btn'
                  onClick={handleUnmarkAsPaid}
                  title="Unmark as paid and unlock price/payment (not available for QuickBooks invoices)"
                >
                  Unmark as Paid
                </button>
              )}
            </div>
          )}
          <button
            type='button'
            className={`actionbar-btn actionbar-btn-quiet ${archivePending ? 'actionbar-btn-pending' : ''}`}
            onClick={handleArchiveToggle}
            disabled={!currentWorkOrder.invoice_number}
            title={archiveTitle}
          >
            {archiveLabel}
          </button>
        </div>

        {/* Phones and tablets: everything but Save is in the More menu */}
        <div className='actionbar-more'>
          <button type='button' className='actionbar-btn' onClick={handleMobileMoreClick} aria-expanded={showMobileMoreDropdown}>
            More {showMobileMoreDropdown ? '▾' : '▴'}
          </button>
          {showMobileMoreDropdown && (
            <div className='actionbar-menu'>
              <div className='actionbar-menu-heading'>
                Invoice{currentWorkOrder.invoice_number ? ` #${currentWorkOrder.invoice_number}` : ''}
              </div>
              {!currentWorkOrder.invoice_number && (
                <button type='button' onClick={menuAction(handleConvertToInvoice)}>Convert to Invoice</button>
              )}
              {canEditInvoice && (
                <button type='button' onClick={menuAction(handleEditInvoice)}>Edit Invoice</button>
              )}
              {currentWorkOrder.invoice_number && (
                <button type='button' onClick={menuAction(handlePrintInvoice)} disabled={loadingInvoicePdf}>
                  {loadingInvoicePdf ? 'Loading Invoice…' : 'Print Invoice'}
                </button>
              )}
              {currentWorkOrder.is_invoiced && (
                <button type='button' onClick={menuAction(handleSendInvoice)}>
                  {currentWorkOrder.invoice_sent_at ? 'Send Invoice Again' : 'Send Invoice'}
                </button>
              )}
              {currentWorkOrder.mark_as_paid && !currentWorkOrder.is_invoiced && (
                <button type='button' onClick={menuAction(handleUnmarkAsPaid)}>Unmark as Paid</button>
              )}
              <div className='actionbar-menu-heading'>Work Order</div>
              <button type='button' onClick={menuAction(handleHistoryClick)}>History</button>
              {!fromJobOrder && currentWorkOrder.job && (
                <button type='button' onClick={menuAction(handleJobOrderClick)}>View Job Order</button>
              )}
              <div className='actionbar-menu-divider' />
              <button
                type='button'
                className={archivePending ? 'actionbar-menu-pending' : ''}
                onClick={menuAction(handleArchiveToggle)}
                disabled={!currentWorkOrder.invoice_number}
                title={archiveTitle}
              >
                {archiveLabel}
              </button>
            </div>
          )}
        </div>

        <button className='actionbar-save' type="submit">Save</button>
      </div>
      {showHistory && (
        <WorkOrderHistory
          workOrderId={workOrder.id}
          onClose={() => setShowHistory(false)}
        />
      )}
      {showUnsavedWarning && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <div className="unsaved-changes-buttons">
              <button type="button" onClick={handleConfirmClose}>Leave</button>
              <button type="button" onClick={handleCancelClose}>Stay</button>
            </div>
          </div>
        </div>
      )}
      {showErrorModal && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>{errorTitle}</h3>
            <p>{errorMessage}</p>
            <div className="unsaved-changes-buttons">
              <button type="button" onClick={handleErrorModalClose}>OK</button>
            </div>
          </div>
        </div>
      )}
    </form>
    {showInvoiceModal && (
      <InvoiceModal
        workOrder={currentWorkOrder}
        editInvoice={editingInvoice}
        customerId={currentWorkOrder.customer?.id ?? currentWorkOrder.customer}
        onClose={() => {
          console.log('Closing invoice modal');
          setShowInvoiceModal(false);
        }}
        onConfirm={handleInvoiceConfirm}
      />
    )}
    {showSendInvoice && (
      <SendInvoiceDialog
        id={currentWorkOrder.id}
        invoiceNumber={currentWorkOrder.invoice_number}
        onClose={() => setShowSendInvoice(false)}
        onSent={handleInvoiceSent}
      />
    )}
    {showPrintInvoice && (
      <div className="print-invoice-overlay">
        <div className="print-invoice-container">
          <div className="print-invoice-header">
            <h3>Print Invoice</h3>
            <div className="print-invoice-buttons">
              <button 
                className="print-button" 
                onClick={async () => {
                  // Mobile-friendly print implementation
                  const printContent = document.querySelector('.print-invoice');
                  
                  if (printContent) {
                    // Check if we're on mobile
                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                    
                    if (isMobile) {
                      // For mobile: Create a print-friendly view that users can manually print
                      const printContent = document.querySelector('.print-invoice');
                      
                      if (printContent) {
                        // Create a new window with print-friendly content
                        const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
                        
                        if (printWindow) {
                          // Mobile-friendly print styles
                          const mobilePrintCSS = `
                            <style>
                              body { 
                                margin: 0; 
                                padding: 20px; 
                                font-family: Arial, sans-serif;
                                background: white;
                                color: #333;
                              }
                              .print-invoice {
                                max-width: none !important;
                                margin: 0 !important;
                                padding: 0.25in !important;
                                font-size: 12px !important;
                                line-height: 1.4;
                                color: #333;
                                background: white;
                                border: 1px solid #ddd;
                                border-radius: 8px;
                              }
                              .invoice-header {
                                display: flex;
                                flex-direction: column;
                                text-align: center;
                                margin-bottom: 1rem;
                              }
                              .company-address {
                                text-align: center;
                                margin-bottom: 10px;
                              }
                              .company-logo-center {
                                position: static !important;
                                transform: none !important;
                                margin: 10px 0 !important;
                              }
                              .company-logo {
                                max-height: 60px;
                                max-width: 150px;
                              }
                              .invoice-info-section {
                                display: flex;
                                justify-content: center;
                                margin-bottom: 1rem;
                              }
                              .invoice-info-right {
                                text-align: center;
                              }
                              .invoice-info-item {
                                display: flex;
                                justify-content: space-between;
                                margin-bottom: 0.3rem;
                                font-size: 0.9rem;
                                min-width: 200px;
                              }
                              .services-table {
                                width: 100%;
                                border-collapse: collapse;
                                font-size: 10px !important;
                              }
                              .services-table th,
                              .services-table td {
                                padding: 0.25rem !important;
                                border: 1px solid #ddd;
                              }
                              .services-table th {
                                background-color: #2c5aa0;
                                color: white;
                              }
                              .footer-section {
                                display: flex;
                                flex-direction: column;
                                text-align: center;
                                margin-top: 2rem;
                              }
                              .print-button-mobile {
                                background: #007bff;
                                color: white;
                                border: none;
                                padding: 12px 24px;
                                border-radius: 6px;
                                font-size: 16px;
                                font-weight: bold;
                                cursor: pointer;
                                margin: 10px;
                                display: inline-block;
                              }
                              .print-button-mobile:hover {
                                background: #0056b3;
                              }
                              .close-print-window {
                                position: absolute;
                                top: 10px;
                                right: 10px;
                                background: none;
                                border: none;
                                font-size: 24px;
                                color: #666;
                                cursor: pointer;
                                padding: 0 10px;
                                line-height: 1;
                                transition: color 0.2s ease;
                                z-index: 1000;
                              }
                              .close-print-window:hover {
                                color: #333;
                              }
                              @media print {
                                .print-button-mobile,
                                .close-print-window {
                                  display: none !important;
                                }
                                body { margin: 0; padding: 0; }
                                .print-invoice { 
                                  max-width: none !important; 
                                  margin: 0 !important; 
                                  padding: 0.25in !important; 
                                  border: none !important;
                                }
                              }
                            </style>
                          `;
                          
                          printWindow.document.write(`
                            <html>
                              <head>
                                <title>Invoice - Work Order #${currentWorkOrder.id}</title>
                                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                ${mobilePrintCSS}
                              </head>
                              <body>
                                <div style="position: relative;">
                                  <button class="close-print-window" onclick="window.close()" title="Close">×</button>
                                  <div style="text-align: center;">
                                    <button class="print-button-mobile" onclick="window.print()">🖨️ Print Invoice</button>
                                  </div>
                                </div>
                                ${printContent.outerHTML}
                              </body>
                            </html>
                          `);
                          
                          printWindow.document.close();
                          printWindow.focus();
                        } else {
                          // Fallback: Show instructions for manual printing
                          alert('📱 Print Instructions:\n\n1. Use your browser\'s menu (⋮) → Print\n2. Or press Ctrl+P (Android) / Cmd+P (iOS)\n3. Select your printer and print\n\nThe invoice is ready to print!');
                        }
                      }
                    } else {
                      // For desktop: Use the original popup window method
                      const printWindow = window.open('', '_blank');
                      
                      if (printWindow) {
                        // Get the CSS content
                        const cssContent = `
                          /* Print Invoice Styles */
                          .print-invoice {
                            max-width: 8.5in;
                            margin: 0 auto;
                            padding: 0.5in;
                            font-family: Arial, sans-serif;
                            line-height: 1.4;
                            color: #333;
                            background: white;
                          }
                          
                          /* Header Section */
                          .invoice-header {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-bottom: 1rem;
                            position: relative;
                          }
                          
                          .company-address {
                            flex: 1;
                            text-align: left;
                          }
                          
                          .company-address p {
                            margin: 0.1rem 0;
                            font-size: 0.8rem;
                            color: #666;
                            line-height: 1.2;
                          }
                          
                          .company-logo-center {
                            position: absolute;
                            left: 50%;
                            top: 0;
                            transform: translateX(-50%);
                            text-align: center;
                          }
                          
                          .company-logo {
                            max-height: 80px;
                            max-width: 200px;
                            margin: 0;
                          }
                          
                          /* Invoice Info Section */
                          .invoice-info-section {
                            display: flex;
                            justify-content: flex-end;
                            margin-bottom: 1rem;
                          }
                          
                          .invoice-info-right {
                            text-align: right;
                          }
                          
                          .invoice-info-item {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 0.3rem;
                            font-size: 0.9rem;
                            min-width: 200px;
                          }
                          
                          .invoice-info-item .label {
                            font-weight: bold;
                            color: #333;
                            margin-right: 0.5rem;
                          }
                          
                          .invoice-info-item .value {
                            color: #333;
                          }
                          
                          /* Bottom Blue Line */
                          .bottom-blue-line {
                            height: 3px;
                            background-color: #2c5aa0;
                            margin: 1rem 0;
                          }
                          
                          /* Bill To Section */
                          .bill-to-section {
                            margin-bottom: 2rem;
                          }
                          
                          .bill-to h3 {
                            color: #2c5aa0;
                            font-size: 1.2rem;
                            margin: 0 0 1rem 0;
                            font-weight: bold;
                          }
                          
                          .customer-info p {
                            margin: 0.3rem 0;
                            font-size: 1rem;
                          }
                          
                          
                          /* Services Section */
                          .services-section {
                            margin-bottom: 2rem;
                          }
                          
                          .services-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 1rem;
                          }
                          
                          .services-table th {
                            background-color: #2c5aa0;
                            color: white;
                            padding: 0.75rem;
                            text-align: left;
                            font-weight: bold;
                            border: 1px solid #2c5aa0;
                          }
                          
                          .services-table td {
                            padding: 0.75rem;
                            border: 1px solid #dee2e6;
                            vertical-align: top;
                          }
                          
                          .services-table tr:nth-child(even) {
                            background-color: #f8f9fa;
                          }
                          
                          .service-description p {
                            margin: 0.2rem 0;
                            font-size: 0.9rem;
                          }
                          
                          /* Totals Section */
                          .totals-section {
                            margin-bottom: 2rem;
                          }
                          
                          .totals-table {
                            max-width: 300px;
                            margin-left: auto;
                          }
                          
                          .total-row {
                            display: flex;
                            justify-content: space-between;
                            padding: 0.5rem 0;
                            border-bottom: 1px solid #dee2e6;
                          }
                          
                          .total-row.total-final {
                            border-top: 2px solid #2c5aa0;
                            border-bottom: 2px solid #2c5aa0;
                            font-size: 1.1rem;
                            margin-top: 0.5rem;
                            padding-top: 0.75rem;
                            padding-bottom: 0.75rem;
                          }
                          
                          .total-row .label {
                            font-weight: bold;
                          }
                          
                          .total-row .value {
                            font-weight: bold;
                          }
                          
                          /* Notes Section */
                          .notes-section {
                            margin-bottom: 2rem;
                            padding: 1rem;
                            background-color: #f8f9fa;
                            border-left: 4px solid #2c5aa0;
                          }
                          
                          .notes-section h3 {
                            color: #2c5aa0;
                            font-size: 1.1rem;
                            margin: 0 0 0.5rem 0;
                            font-weight: bold;
                          }
                          
                          .notes-textarea {
                            width: 100%;
                            min-height: 80px;
                            padding: 0.75rem;
                            border: 1px solid #dee2e6;
                            border-radius: 4px;
                            font-size: 0.9rem;
                            font-family: inherit;
                            resize: vertical;
                            background-color: white;
                            transition: border-color 0.2s ease;
                          }
                          
                          .notes-textarea:focus {
                            outline: none;
                            border-color: #2c5aa0;
                            box-shadow: 0 0 0 2px rgba(44, 90, 160, 0.2);
                          }
                          
                          .notes-textarea::placeholder {
                            color: #6c757d;
                            font-style: italic;
                          }
                          
                          
                          /* Footer Section */
                          .footer-section {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-top: 3rem;
                            padding-top: 2rem;
                            border-top: 1px solid #dee2e6;
                          }
                          
                          .payment-terms h4 {
                            color: #2c5aa0;
                            font-size: 1.1rem;
                            margin: 0 0 0.5rem 0;
                            font-weight: bold;
                          }
                          
                          .payment-terms p {
                            margin: 0.2rem 0;
                            font-size: 0.9rem;
                          }
                          
                          .contact-info {
                            text-align: right;
                          }
                          
                          .contact-info p {
                            margin: 0.2rem 0;
                            font-size: 0.9rem;
                            color: #666;
                          }
                        `;
                        
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>Invoice - Work Order #${currentWorkOrder.id}</title>
                              <style>${cssContent}</style>
                            </head>
                            <body>
                              ${printContent.outerHTML}
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.focus();
                        printWindow.print();
                        printWindow.close();
                      }
                    }
                  }
                }}
              >
                Print
              </button>
              <button 
                className="workorderhistorybutton" 
                onClick={() => setShowPrintInvoice(false)}
              >
                Close
              </button>
            </div>
          </div>
          <PrintInvoice workOrder={currentWorkOrder} />
        </div>
      </div>
    )}
    </>
  );
};

export default WorkOrder;